/**
 * The sentence a user reads when a feature-access restriction refused them.
 *
 * Feature access is an organization-admin GRANT, so the only thing that ever
 * resolves this refusal is an admin changing a grant. The sentence therefore
 * names what the admin has to change — the feature, and the caller's own groups
 * that leave it out — and never offers a retry or an upgrade, neither of which
 * can come true.
 *
 * A group whose access was set up before the feature existed is told apart from
 * one that withholds it on purpose (`new_not_granted` against `not_granted`),
 * because those are different asks: one is "nobody has looked at this yet", the
 * other is "an admin decided this". Both still end in the same ask.
 *
 * Pure, and takes `t` and the language as parameters rather than calling a
 * hook — the idiom `capability-outage-copy.ts` uses — so the error handler,
 * which is documented pure and runs outside React, reaches the same sentence a
 * component renders.
 */

import { GroupFeatureStatus } from "@nexus/types/domain"
import type { TFunction } from "i18next"

import type { FeatureRestriction } from "./feature-restriction"

const KEY = "common.feature-access.restricted"

/**
 * Joins already-quoted group names the way the language joins a list.
 *
 * `Intl.ListFormat` rather than a `", "` join, so the last two names read
 * "A and B" in English and "A et B" in French. An empty, malformed or
 * unsupported tag degrades to English rather than throwing on a customer's
 * screen — `supportedLocalesOf` throws a `RangeError` on a malformed one, and
 * an uninitialised i18next reports no language at all.
 */
function listLocale(language: string): string {
  if (language === "") return "en"
  try {
    return Intl.ListFormat.supportedLocalesOf([language])[0] ?? "en"
  } catch {
    return "en"
  }
}

function joinNames(names: readonly string[], language: string): string {
  return new Intl.ListFormat(listLocale(language), { style: "long", type: "conjunction" }).format(
    names
  )
}

function quotedGroups(
  groups: FeatureRestriction["groups"],
  t: TFunction,
  language: string
): string {
  return joinNames(
    groups.map((group) => t(`${KEY}.group-name`, { name: group.name })),
    language
  )
}

/**
 * Resolves the refusal's sentence for the active language.
 *
 * @param restriction what the server said limits the caller
 * @param t a translation function bound to `language`
 * @param language the BCP 47 tag `t` resolves, used to join the group names
 */
export function featureRestrictionMessage(
  restriction: FeatureRestriction,
  t: TFunction,
  language: string
): string {
  const decided = restriction.groups.filter(
    (group) => group.featureStatus === GroupFeatureStatus.NOT_GRANTED
  )
  const undecided = restriction.groups.filter(
    (group) => group.featureStatus === GroupFeatureStatus.NEW_NOT_GRANTED
  )

  const sentences = [
    restriction.featureName === null
      ? t(`${KEY}.lead-unnamed`)
      : t(`${KEY}.lead`, { feature: restriction.featureName })
  ]
  if (decided.length > 0) {
    sentences.push(
      t(`${KEY}.decided`, { count: decided.length, groups: quotedGroups(decided, t, language) })
    )
  }
  if (undecided.length > 0) {
    sentences.push(
      t(`${KEY}.new`, { count: undecided.length, groups: quotedGroups(undecided, t, language) })
    )
  }
  if (restriction.userGrant) {
    sentences.push(t(`${KEY}.user-grant`))
  }
  sentences.push(t(`${KEY}.ask`))

  return sentences.join(" ")
}
